import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { AlertDialog } from './AlertDialog';
import { ConfirmDialog } from './ConfirmDialog';
import { InfoDialog } from './InfoDialog';
import { ProcessingOverlay } from './ProcessingOverlay';

/**
 * 四個共用對話框的呼叫介面 —— 讓頁面像原系統一樣「一行叫出來」，不用自己管 open state。
 *
 * 原系統的對應寫法（`js-my/common.js`）：
 *   `sysConfirm(msg, yes, no)`      `:356`  → `await confirm(msg)`
 *   `showAlertDialog(content, cb)`  `:340`  → `await alert(content)`
 *   `showInfoDialog(content, cb)`   `:317`  → `await info(content)`
 *   `showProcessing()`              `:443`  → `processing.show()` / `.hide()` / `.wrap(promise)`
 *
 * 原系統用 callback，這裡改成 Promise：`sysConfirm(msg, yes, no)` 的兩個分支在
 * Promise 版本就是 `if (await confirm(msg))`，呼叫端少一層縮排、也不會忘了寫 no 分支。
 *
 * ponytail: 同時只維持一個對話框（jquery-confirm 本來就是 modal，原系統不可能疊兩層），
 * 不做佇列。若在已開啟時再叫一次，前一個會以「取消／關閉」結束再開新的 ——
 * 這是明確的取捨，不是漏掉。升級路徑：真的需要疊層時把 state 換成陣列。
 */
export interface DialogTextOptions {
  /** 覆寫標題。 */
  title?: string;
}

export interface ProcessingApi {
  show(): void;
  hide(): void;
  /** 包住一個非同步工作：開始時顯示遮罩，結束（含丟錯）時關閉。 */
  wrap<T>(work: Promise<T>): Promise<T>;
}

export interface DialogsApi {
  /** 破壞性操作確認。回傳 true＝按了「確定」。 */
  confirm(message: string): Promise<boolean>;
  /** 失敗／驗證訊息。 */
  alert(message: ReactNode, options?: DialogTextOptions): Promise<void>;
  /** 成功訊息。 */
  info(message: ReactNode, options?: DialogTextOptions): Promise<void>;
  processing: ProcessingApi;
}

type DialogRequest =
  | { kind: 'confirm'; message: string; title: undefined; resolve(value: boolean): void }
  | { kind: 'alert'; message: ReactNode; title: string | undefined; resolve(value: boolean): void }
  | { kind: 'info'; message: ReactNode; title: string | undefined; resolve(value: boolean): void };

const DialogContext = createContext<DialogsApi | null>(null);

export interface DialogProviderProps {
  /** 對話框的 data-ui-id 前綴。全站只會有一組，預設值即可。 */
  uiId?: string;
  children: ReactNode;
}

export function DialogProvider({ uiId = 'global-dialog', children }: DialogProviderProps) {
  const [request, setRequest] = useState<DialogRequest | null>(null);
  const [processingCount, setProcessingCount] = useState(0);
  // 用 ref 讀「目前開著的請求」，避免 open()／close() 進到 useCallback 的相依鏈。
  const requestRef = useRef<DialogRequest | null>(null);

  const settle = useCallback((value: boolean) => {
    const current = requestRef.current;
    requestRef.current = null;
    setRequest(null);
    current?.resolve(value);
  }, []);

  const open = useCallback(
    (next: DialogRequest) => {
      // 已經開著就先收掉前一個（見上方 ponytail 註解）。
      if (requestRef.current !== null) settle(false);
      requestRef.current = next;
      setRequest(next);
    },
    [settle],
  );

  const api = useMemo<DialogsApi>(() => {
    const processing: ProcessingApi = {
      show: () => setProcessingCount((n) => n + 1),
      hide: () => setProcessingCount((n) => Math.max(0, n - 1)),
      wrap: async <T,>(work: Promise<T>): Promise<T> => {
        setProcessingCount((n) => n + 1);
        try {
          return await work;
        } finally {
          setProcessingCount((n) => Math.max(0, n - 1));
        }
      },
    };

    return {
      confirm: (message) =>
        new Promise<boolean>((resolve) => {
          open({ kind: 'confirm', message, title: undefined, resolve });
        }),
      alert: (message, options) =>
        new Promise<void>((resolve) => {
          open({
            kind: 'alert',
            message,
            title: options?.title,
            resolve: () => resolve(),
          });
        }),
      info: (message, options) =>
        new Promise<void>((resolve) => {
          open({
            kind: 'info',
            message,
            title: options?.title,
            resolve: () => resolve(),
          });
        }),
      processing,
    };
  }, [open]);

  return (
    <DialogContext.Provider value={api}>
      {children}
      <ConfirmDialog
        uiId={`${uiId}-confirm`}
        open={request?.kind === 'confirm'}
        message={request?.kind === 'confirm' ? request.message : ''}
        onConfirm={() => settle(true)}
        onCancel={() => settle(false)}
      />
      <AlertDialog
        uiId={`${uiId}-alert`}
        open={request?.kind === 'alert'}
        message={request?.kind === 'alert' ? request.message : null}
        {...(request?.kind === 'alert' && request.title !== undefined
          ? { title: request.title }
          : {})}
        onClose={() => settle(false)}
      />
      <InfoDialog
        uiId={`${uiId}-info`}
        open={request?.kind === 'info'}
        message={request?.kind === 'info' ? request.message : null}
        {...(request?.kind === 'info' && request.title !== undefined
          ? { title: request.title }
          : {})}
        onClose={() => settle(false)}
      />
      <ProcessingOverlay uiId={`${uiId}-processing`} open={processingCount > 0} />
    </DialogContext.Provider>
  );
}

/** 取得四個共用對話框的呼叫介面。必須包在 <DialogProvider> 內。 */
export function useDialogs(): DialogsApi {
  const api = useContext(DialogContext);
  if (api === null) {
    throw new Error('useDialogs() 必須在 <DialogProvider> 內使用');
  }
  return api;
}
